import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, defineAsyncComponent, watch } from 'vue';
import AdUnit from '@/components/AdUnit.vue';
import LazyClientShow from '@/components/LazyClientShow.vue';
import GuideList from '@/components/editorial/GuideList.vue';
import TitleDetailJumpLinksV1 from '@/pages/titleDetailV1/v1/TitleDetailJumpLinksV1.vue';
import SectionContainer from '@/pages/titleDetailV1/components/SectionContainer.vue';
import { TitleDetailArticleFragment } from '@/pages/graphql/queries/GetTitleDetailArticle.query';
import { TitleDetailSnippetFragment } from '@/pages/graphql/queries/GetTitleDetailSnippets.query';
import { GetNodeTitleDetailsQuery } from '@/pages/graphql/queries/GetNodeTitleDetails.query';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import SimilarTitles from '@/components/SimilarTitles.vue';
import BuyBoxNew from '@/components/buybox-new/BuyBoxNew.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import {
	useTitleDetailInfo,
	isTitleShow,
	isTitleSeason,
} from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { useUserStore, useAppStore, useLanguageStore, useExperimentStore } from '@/helpers/composables/useStores';
import { NewBuyBoxView } from '@/stores/user.store';
import { streamingChartCountries } from '@/components/details/streaming-chart/streaming-chart-config';
import TitleDetailHeroV1 from '@/pages/titleDetailV1/v1/hero/TitleDetailHeroV1.vue';
import TitleDetailTitleClipsV1 from '@/pages/titleDetailV1/v1/TitleDetailTitleClipsV1.vue';
import BuyBoxBanner from '@/components/buybox-new/BuyBoxBanner.vue';
import TitleDetailTitleSidebarV1 from '@/pages/titleDetailV1/v1/sidebar/TitleDetailTitleSidebarV1.vue';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { TitleDetail } from '@/interfaces/title-details-graphql';

import PromotedBuyBox from '@/components/buybox/PromotedBuyBox.vue';
import SeasonListV1 from '@/pages/titleDetailV1/v1/SeasonListV1.vue';
import BuyBoxTypeSwitcher from '@/components/buybox-new/BuyBoxTypeSwitcher.vue';

// AQTVTitleDetailPlacememts
import { usePromotion } from '@/helpers/composables/usePromotion';
import AQTVTitleDetailPlacements, {
	AQTVTitleDetailPlacementsName,
	AQTVTitleDetailPlacementsVariants,
} from '@/components/experiments/AQTVTitleDetailPlacememts';
import { appleQualityTvCountries } from '@/components/buybox/QualityTvRow/data';
import { useMounted } from '@vueuse/core';
import { ObjectType } from '@/@types/graphql-types';
// AQTVTitleDetailPlacememts


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailV1',
  props: {
    titleDetailNode: null,
    promotedOffers: { default: () => [] },
    metaData: null,
    article: null,
    snippets: null,
    genresByShortName: null,
    isAVODActive: { type: Boolean },
    additionalContexts: null,
    hidePromotedBuyBox: { type: Boolean },
    _sponsoredAd: { default: undefined },
    entityId: null,
    isLoading: { type: Boolean },
    isPerceivedLoading: { type: Boolean }
  },
  emits: ['buyboxLoading', 'loadAllEpisodes'],
  setup(__props: any, { emit: emits }) {

const props = __props as { titleDetailNode: AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Movie' | 'Show' | 'Season'> | undefined, promotedOffers: TitleOfferFragment[], metaData: any, article: TitleDetailArticleFragment, snippets: TitleDetailSnippetFragment[], genresByShortName: any, isAVODActive: boolean, additionalContexts: SnowplowContext[], hidePromotedBuyBox: boolean, _sponsoredAd: SponsoredAdFragment | undefined, entityId?: string | undefined, isLoading?: boolean, isPerceivedLoading: boolean };

const TitleDetailLazyWhyToWatchV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailLazyWhyToWatchV1.vue')
);
const TitleDetailStreamingChartV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/streaming-chart/TitleDetailStreamingChartV1.vue')
);
const TitleSynopsis = defineAsyncComponent(() => import('@/components/details/TitleSynopsis.vue'));

const TitleDetailAccordion = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/components/TitleDetailAccordion.vue')
);
const TitleDetailArticleBlockV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailArticleBlockV1.vue')
);
const TitleDetailProductionNewsBlockV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailProductionNewsBlockV1.vue')
);
const ArticleBlock = defineAsyncComponent(() => import('@/components/details/editorial/ArticleBlock.vue'));
const UpcomingContent = defineAsyncComponent(() => import('@/components/upcoming/UpcomingContent.vue'));
const EpisodesList = defineAsyncComponent(() => import('@/components/EpisodesList.vue'));
const TitleCredits = defineAsyncComponent(() => import('@/components/details/TitleCredits.vue'));
const SpinningText = defineAsyncComponent(() => import('@/components/details/SpinningText.vue'));
const TitleDetailSimilarTitles = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailSimilarTitles.vue')
);
const BuyBox = defineAsyncComponent(() => import('@/components/buybox/BuyBox.vue'));
// need to import these immediately because it's above the fold and could cause CLS
const activeClipId = ref<string | null>(null);
const streamingChart = ref(null);
const container = ref(null);

const { newBuyboxView } = useUserStore();
const { isSeoUser } = useAppStore();
const { country } = useLanguageStore();
const { t } = useI18n();




const titleDetailNodeComputed = computed(() => props.titleDetailNode);
const {
	title,
	titleName,
	titleId,
	titleObjectId,
	titleObjectType,
	seasonName,
	seasonNumber,
	seasonTotalEpisodeCount,
	isShow,
	isSeason,
	recentEpisodes,
	recentEpisodesAvailable,
	isTitleUpcoming,
	showDetails,
	originalTitle,
} = useTitleDetailInfo(titleDetailNodeComputed);

const hasTrailers = computed(() => (props.titleDetailNode?.content.clips.length ?? 0) > 0);
const displayStreamingChart = computed(() => streamingChartCountries.includes(country.value));
const isListSponsoredAd = computed(() => props._sponsoredAd?.campaign?.node?.__typename === 'GenericTitleList');
const isShadowCampaign = computed(() => !!props._sponsoredAd?.bidId && props._sponsoredAd?.campaign === null);
const hasActiveSRCampaign = computed(
	() => props._sponsoredAd?.holdoutGroup == false && props._sponsoredAd?.campaign != null
);
const visibleSectionState = computed(() => {
	return {
		trailers: !!(hasTrailers.value && props.titleDetailNode),
		'production-news': !!props.article?.content.productionNews,
		'what-to-know': !!props.article?.content.whatToKnow,
		'why-to-watch': props.snippets.length > 0,
		synopsis: !!showSynopsis.value || !!props.article,
		'streaming-chart': !!streamingChart.value,
		'episodes-lists': !isTitleUpcoming.value && (isSeason.value || isShow.value),
		'regular-buybox': !(isTitleUpcoming.value && !showNewBuyBox.value),
		'similar-content': showSimilarTitles.value,
	};
});

const showSynopsis = computed(
	() =>
		!(props.metaData?.htmlContent?.detail_heading != null && props.metaData?.htmlContent?.detail_content != null) &&
		!!props.titleDetailNode?.content.shortDescription
);

const showSimilarTitles = computed(() => !!props.titleDetailNode && !props.metaData?.htmlContent.hide_similar_titles);

// AMZ fix on apple titles
const hasApple = computed(() => props.titleDetailNode?.offers.some(offer => offer.package.packageId === 350));

const filteredPromotedOfers = computed(() =>
	props.promotedOffers.filter(offer => (hasApple.value ? offer.package.packageId !== 9 : true))
);
// AMZ fix on apple titles

const aboutHeadline = computed(() => {
	return isSeason.value
		? t('WEBAPP_ABOUT_THE_SEASON')
		: isShow.value
		? t('WEBAPP_ABOUT_THE_TV_SHOW')
		: t('WEBAPP_ABOUT_THE_MOVIE');
});

const isUSShow = computed(() => isShow.value && country.value === 'US');

const showNewBuyBox = computed(
	() =>
		newBuyboxView.value !== NewBuyBoxView.GRID &&
		(titleObjectType.value === ObjectType.Movie || isUSShow.value) &&
		!isSeason.value
);

const episodesTitle = computed(() => {
	// only show this for now for show page release - tbc
	return t('WEBAPP_WATCH_NEWEST_EPISODES');
	// return recentEpisodesAvailable.value ? t('WEBAPP_RECENT_EPISODES') : t('WEBAPP_WATCH_NEWEST_EPISODES');
});

const maxEpisodes = computed(() =>
	recentEpisodesAvailable.value ? recentEpisodes.value.length : seasonTotalEpisodeCount.value
);

const episodes = computed(() =>
	recentEpisodesAvailable.value
		? recentEpisodes.value
		: (props.titleDetailNode as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Season'>).episodes || []
);

function removeAdClicked() {
	// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
	// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'RemoveAds' });
}
function loadAllEpisodes() {
	emits('loadAllEpisodes', maxEpisodes.value);
	TrackingHelper.trackEvent('userinteraction', {
		action: 'click',
		label: 'load_all_episodes',
	});

	// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'load_all_episodes' });
}

// AQTV_TITLEDETAIL_PLACEMENTS
const isMounted = useMounted();
const { inPromotionCatalogue, promotionOffer } = usePromotion({
	offers: () => props.titleDetailNode?.offers as TitleOfferFragment[],
	title: () => props.titleDetailNode as TitleDetail,
});
const { activeVariantsWithControlGroup } = useExperimentStore();

const appleQualityTvExpVariant = computed(() =>
	isMounted.value &&
	titleObjectType.value === ObjectType.Movie &&
	newBuyboxView.value === NewBuyBoxView.LIST &&
	appleQualityTvActive.value
		? (activeVariantsWithControlGroup.value[AQTVTitleDetailPlacementsName] as AQTVTitleDetailPlacementsVariants)
		: null
);

const appleQualityTvActive = computed(() => {
	return (
		!inPromotionCatalogue.value &&
		appleQualityTvCountries.includes(country.value) &&
		promotionOffer?.value?.package.packageId === 350
	);
});

watch(
	() => appleQualityTvExpVariant.value,
	() => {
		if (!!appleQualityTvExpVariant.value) {
			AQTVTitleDetailPlacements.trackEvent({
				action: 'impression',
			});
		}
	},
	{ immediate: true }
);

// AQTV_TITLEDETAIL_PLACEMENTS

return { __sfc: true,TitleDetailLazyWhyToWatchV1, TitleDetailStreamingChartV1, TitleSynopsis, TitleDetailAccordion, TitleDetailArticleBlockV1, TitleDetailProductionNewsBlockV1, ArticleBlock, UpcomingContent, EpisodesList, TitleCredits, SpinningText, TitleDetailSimilarTitles, BuyBox, activeClipId, streamingChart, container, newBuyboxView, isSeoUser, country, t, emits, props, titleDetailNodeComputed, title, titleName, titleId, titleObjectId, titleObjectType, seasonName, seasonNumber, seasonTotalEpisodeCount, isShow, isSeason, recentEpisodes, recentEpisodesAvailable, isTitleUpcoming, showDetails, originalTitle, hasTrailers, displayStreamingChart, isListSponsoredAd, isShadowCampaign, hasActiveSRCampaign, visibleSectionState, showSynopsis, showSimilarTitles, hasApple, filteredPromotedOfers, aboutHeadline, isUSShow, showNewBuyBox, episodesTitle, maxEpisodes, episodes, removeAdClicked, loadAllEpisodes, isMounted, inPromotionCatalogue, promotionOffer, activeVariantsWithControlGroup, appleQualityTvExpVariant, appleQualityTvActive, AdUnit, LazyClientShow, GuideList, TitleDetailJumpLinksV1, SectionContainer, SimilarTitles, BuyBoxNew, isTitleShow, isTitleSeason, TitleDetailHeroV1, TitleDetailTitleClipsV1, BuyBoxBanner, TitleDetailTitleSidebarV1, PromotedBuyBox, SeasonListV1, BuyBoxTypeSwitcher }
}

})